'use client';
import { vehicleLoanCalcTool } from '../schemas';
import { z } from 'zod';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import InputField from '@/src/features/ui/react-hook-form/ui/input-field';
import { useRouter } from 'next/navigation';
import { Button } from '@/src/features/ui/button/button';
import { ResponseAttribute, ResponseAttributeOption } from '@/src/interfaces/data-contracts';
import classNames from 'classnames';
import { CustomRequestEvent, DataLayerService } from '@/lib/data-layer/data-layer';
import { DataLayerEventCategoryEnum } from '@/lib/data-layer/types';
import fmt from '@/src/utils/helpers/number-format';
import { CurrencyTypeMissing } from '@/src/interfaces/missing-types';

type VehicleLoanSchema = z.infer<typeof vehicleLoanCalcTool>;

type TCalculationTools = {
  Amount?: string;
  Maturity?: string;
  CarStatus?: string;
  maturities: ResponseAttribute;
  detailPage?: boolean;
  bankPage?: boolean;
  bankSeoUrl?: string;
  productSeoUrl?: string;
};

const vehiclestatus = [
  { id: 1, name: '0 Km' },
  { id: 2, name: '2. El' },
];

const dataLayer = new DataLayerService();

const CalculationTool: React.FC<TCalculationTools> = ({
  maturities,
  Amount,
  Maturity,
  CarStatus,
  detailPage = false,
  bankPage = false,
  bankSeoUrl,
  productSeoUrl,
}) => {
  const router = useRouter();

  const methods = useForm<VehicleLoanSchema>({
    resolver: zodResolver(vehicleLoanCalcTool),
    mode: 'all',
    defaultValues: {
      Amount: Amount ? fmt(Number(Amount), CurrencyTypeMissing.TRY, false, false, 0) : '',
      Maturity: Maturity ?? '',
      VehicleStatus: CarStatus ? String(CarStatus) : '1',
    },
  });

  const onSubmit = (formData: VehicleLoanSchema) => {
    try {
      dataLayer.sendEvent(
        new CustomRequestEvent(
          `${formData.Amount}_${formData.Maturity}_${formData.VehicleStatus}`,
          'Ticari Taşıt Kredisi',
          DataLayerEventCategoryEnum.QUERY,
          ''
        )
      );

      let url = '';
      if (detailPage) {
        url = `/ticari-tasit-kredisi/${bankSeoUrl}/${productSeoUrl}?Amount=${formData.Amount}&Maturity=${formData.Maturity}&CarStatus=${formData.VehicleStatus}`;
      } else if (bankPage) {
        url = `/ticari-tasit-kredisi/${bankSeoUrl}?Amount=${formData.Amount}&Maturity=${formData.Maturity}&CarStatus=${formData.VehicleStatus}`;
      } else {
        url = `/ticari-tasit-kredisi/sorgulama?Amount=${formData.Amount}&Maturity=${formData.Maturity}&CarStatus=${formData.VehicleStatus}`;
      }
      router.push(url);
    } catch (error) {
      console.warn(error);
    }
  };

  return (
    <section data-testid="calculationTool">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-row flex-wrap justify-between gap-4 pb-6 md:gap-8 md:pb-11"
          data-testid="calculationForm"
        >
          <InputField
            required
            label="Kredi Tutarı"
            overrideLabelClassName="text-xs font-semibold md:text-sm"
            append={<span className="relative right-[6px] text-base font-semibold text-black1000">TL</span>}
            name="Amount"
            placeholder="Kredi tutarı"
            type="price"
            maxValue={50000000}
            overrideErrorClassName="text-xs text-error font-semibold mt-2"
            overrideClassName="w-[193px] md:w-[248px] grow"
            noPunc
          />
          <InputField
            label="Vade"
            type="select"
            name="Maturity"
            showNoneOption="Seçiniz"
            overrideClassName="w-[102px] md:w-[256px] grow"
            selectElementNoneOptionColor="text-black800"
            onChange={(e) => methods.setValue('Maturity', e.target.value, { shouldValidate: true })}
          >
            {maturities?.options?.map((item: ResponseAttributeOption) => (
              <option key={item.id} value={String(item.code)} data-testid="maturityOption">
                {item.name}
              </option>
            ))}
          </InputField>
          <InputField
            label="Araç Durumu"
            type="select"
            name="VehicleStatus"
            showNoneOption="Seçiniz"
            selectElementNoneOptionColor="text-black800"
            onChange={(e) => methods.setValue('VehicleStatus', e.target.value, { shouldValidate: true })}
          >
            {vehiclestatus.map((item) => (
              <option key={`${item.id}-${item.name}`} value={String(item.id)}>
                {item.name}
              </option>
            ))}
          </InputField>
          <Button
            type="submit"
            className={classNames('h-[52px] w-full rounded-lg md:mt-[27px] md:h-[56px] md:w-[256px]')}
            text="Yeniden Hesapla"
            disabled={!methods.formState.isValid}
            data-testid="calculateButton"
          />
        </form>
      </FormProvider>
    </section>
  );
};

export default CalculationTool;
